import { createAction, props } from '@ngrx/store';
import {
  OnboardedVehicleDto,
  OrganizationAgrirouterAccountDto,
  PagedResponseOfOnboardedVehicleDto, PagedResponseOfTaskDataFileDto, PagedResponseOfVehicleDto,
} from '@services/api/sdf-agrirouter-api.service';
import { Tractor } from '@models/tractor';

// Account Actions
export const loadAccounts = createAction(
  'loadAccounts',
  props<{ organizationId: string }>(),
);

export const loadAccountsSuccess = createAction(
  'loadAccountsSuccess',
  props<{ accounts: OrganizationAgrirouterAccountDto[] }>(),
);

export const loadAccountsFailure = createAction(
  'loadAccountsFailure',
  props<{ error: any }>(),
);

export const addAccount = createAction(
  'addAccount',
  props<{ organizationId: string; accountName: string }>(),
);

export const addAccountSuccess = createAction(
  'addAccountSuccess',
  props<{ authUrl: string }>(),
);

export const addAccountFailure = createAction(
  'addAccountFailure',
  props<{ error: any }>(),
);

export const removeAccount = createAction(
  'removeAccount',
  props<{ accountId: string }>(),
);

export const removeAccountSuccess = createAction(
  'removeAccountSuccess',
  props<{ accountId: string }>(),
);

export const removeAccountFailure = createAction(
  'removeAccountFailure',
  props<{ error: any }>(),
);

export const selectAccount = createAction(
  'selectAccount',
  props<{ account: OrganizationAgrirouterAccountDto }>(),
);

export const setSelectedVin = createAction(
  'setSelectedVin',
  props<{ selectedVin: string }>(),
);

export const setOnboardedVehiclesLoading = createAction(
  'setOnboardedVehiclesLoading',
  props<{ isLoading: boolean }>(),
);

export const setNotOnboardedVehiclesLoading = createAction(
  'setNotOnboardedVehiclesLoading',
  props<{ isLoading: boolean }>(),
);

export const setSentFilesLoading = createAction(
  'setSentFilesLoading',
  props<{ isLoading: boolean }>(),
);

export const setReceivedFilesLoading = createAction(
  'setReceivedFilesLoading',
  props<{ isLoading: boolean }>(),
);

export const setOnboardedVehiclesFilters = createAction(
  'setOnboardedVehiclesFilters',
  props<{ filters: { qSearch?: string } }>(),
);

export const setOnboardedVehiclesPaginator = createAction(
  'setOnboardedVehiclesPaginator',
  props<{ paginator: { page: number, pageSize: number } }>(),
);

export const setNotOnboardedVehiclesPaginator = createAction(
  'setNotOnboardedVehiclesPaginator',
  props<{ paginator: { page: number, pageSize: number } }>(),
);

export const setSentFilesPaginator = createAction(
  'setSentFilesPaginator',
  props<{ paginator: { page: number, pageSize: number } }>(),
);

export const setReceivedFilesPaginator = createAction(
  'setReceivedFilesPaginator',
  props<{ paginator: { page: number, pageSize: number } }>(),
);

export const setOnboardedVehiclesList = createAction(
  'setOnboardedVehiclesList',
  props<{ response: PagedResponseOfOnboardedVehicleDto }>(),
);

export const setNotOnboardedVehiclesList = createAction(
  'setNotOnboardedVehiclesList',
  props<{ response: PagedResponseOfVehicleDto }>(),
);

export const setSentFilesList = createAction(
  'setSentFilesList',
  props<{ response: PagedResponseOfTaskDataFileDto }>(),
);

export const setReceivedFilesList = createAction(
  'setReceivedFilesList',
  props<{ response: PagedResponseOfTaskDataFileDto }>(),
);

export const getOnboardedVehiclesList = createAction(
  'getOnboardedVehiclesList',
);

export const getNotOnboardedVehiclesList = createAction(
  'getNotOnboardedVehiclesList',
  props<{ accountId: string }>(),
);

export const getSentFilesList = createAction(
  'getSentFilesList',
);

export const getReceivedFilesList = createAction(
  'getReceivedFilesList',
);

// Onboard/Offboard Vehicle Actions
export const onboardVehicle = createAction(
  'onboardVehicle',
  props<{ vin: string; agrirouterAccountId: string }>(),
);

export const offboardVehicle = createAction(
  'offboardVehicle',
  props<{ organizationId: string; agrirouterAccountId: string; vin: string }>(),
);

// File Actions (for details component)
export const loadFilesFromAgrirouter = createAction(
  'loadFilesFromAgrirouter',
  props<{ accountId: string; vin: string }>(),
);

export const loadFilesFromAgrirouterSuccess = createAction(
  'loadFilesFromAgrirouterSuccess',
  props<{ response: PagedResponseOfTaskDataFileDto }>(),
);

export const loadFilesFromAgrirouterFailure = createAction(
  'loadFilesFromAgrirouterFailure',
  props<{ error: any }>(),
);

export const loadFilesToAgrirouter = createAction(
  'loadFilesToAgrirouter',
  props<{ accountId: string; vin: string }>(),
);

export const loadFilesToAgrirouterSuccess = createAction(
  'loadFilesToAgrirouterSuccess',
  props<{ response: PagedResponseOfTaskDataFileDto }>(),
);

export const loadFilesToAgrirouterFailure = createAction(
  'loadFilesToAgrirouterFailure',
  props<{ error: any }>(),
);

export const deleteFile = createAction(
  'deleteFile',
  props<{ organizationId: string; fileId: string; direction: 'sent' | 'received' }>(),
);

export const deleteFileSuccess = createAction(
  'deleteFileSuccess',
  props<{ fileId: string; direction: 'sent' | 'received' }>(),
);

export const deleteFileFailure = createAction(
  'deleteFileFailure',
  props<{ error: any }>(),
);

export const downloadFile = createAction(
  'downloadFile',
  props<{ fileId: string }>(),
);

export const downloadFileSuccess = createAction(
  'downloadFileSuccess',
  props<{ url: string }>(),
);

export const downloadFileFailure = createAction(
  'downloadFileFailure',
  props<{ error: any }>(),
);

// Loading State Actions
export const setLoadingAccounts = createAction(
  'setLoadingAccounts',
  props<{ value: boolean }>(),
);

export const setLoadingOnboardedVehicles = createAction(
  'setLoadingOnboardedVehicles',
  props<{ value: boolean }>(),
);

export const setLoadingNotOnboardedVehicles = createAction(
  'setLoadingNotOnboardedVehicles',
  props<{ value: boolean }>(),
);

export const setLoadingSentFiles = createAction(
  'setLoadingSentFiles',
  props<{ value: boolean }>(),
);

export const setLoadingReceivedFiles = createAction(
  'setLoadingReceivedFiles',
  props<{ value: boolean }>(),
);

export const setOnboardedPaginator = createAction(
  'setOnboardedPaginator',
  props<{ page: number, pageSize: number }>(),
);

export const setNotOnboardedPaginator = createAction(
  'setNotOnboardedPaginator',
  props<{ page: number, pageSize: number }>(),
);

export const setReceivedPaginator = createAction(
  'setReceivedPaginator',
  props<{ page: number, pageSize: number }>(),
);

export const setSentPaginator = createAction(
  'setSentPaginator',
  props<{ page: number, pageSize: number }>(),
);

export const setQSearch = createAction(
  'setQSearch',
  props<{ q: string }>(),
);

// Reset Actions
export const resetConnectionsState = createAction(
  'resetConnectionsState',
);

export const resetFilesState = createAction(
  'resetFilesState',
);

